import { observer } from 'mobx-react-lite';
import { calendarPrintPanelPrintLabel } from 'owa-locstrings/lib/strings/calendarprintpanelprintlabel.locstring.json';
import {
    printModalLabel,
    calendarPrintPanelCancelLabel,
    printModalCloseIconTitle,
} from './CalendarPrintDialog.base.locstring.json';
import loc from 'owa-localize';
import * as React from 'react';

import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import type {
    CalendarPrintDialogProps,
    CalendarPrintHandle,
    CalendarPrintDialogStyleProps,
    CalendarPrintDialogStyles,
} from './CalendarPrintDialog.types';
import { CalendarPrintPreview } from '../CalendarPrintPreview';
import { CalendarPrintSettings } from '../CalendarPrintSettings';
import type { CalendarViewSettings, PrintSettings, WeekLayout } from '..';
import { PrimaryButton, DefaultButton, IconButton } from '@fluentui/react/lib/Button';
import { isBrowserFirefox } from 'owa-user-agent/lib/userAgent';
import { ControlIcons } from 'owa-control-icons';
import type { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { logUsage } from 'owa-analytics';
import print from 'owa-printableiframe-view/lib/components/PrintableIframe';

const getClassNames = classNamesFunction<
    CalendarPrintDialogStyleProps,
    CalendarPrintDialogStyles
>();

import bindClassNames from 'classnames';

export default observer(
    function CalendarPrintDialogBase<TEvent>(
        props: CalendarPrintDialogProps<TEvent>,
        ref?: React.Ref<CalendarPrintHandle>
    ) {
        const [calendarId, setCalendarId] = React.useState<string>(props.printSettings.calendarId);
        const [view, setView] = React.useState<DateRangeType>(props.printSettings.view);
        const [weekLayout, setWeekLayout] = React.useState<WeekLayout>(
            props.printSettings.weekLayout
        );
        const [agenda, setAgenda] = React.useState<boolean>(props.printSettings.agenda);
        const [notes, setNotes] = React.useState<boolean>(props.printSettings.notes);
        const [miniMonth, setMiniMonth] = React.useState<boolean>(props.printSettings.miniMonth);
        const [fromHour, setFromHour] = React.useState<number>(props.printSettings.fromHour);
        const [toHour, setToHour] = React.useState<number>(props.printSettings.toHour);
        const [hideWorkWeekView, setHideWorkWeekView] = React.useState<boolean>(
            props.printSettings.hideWorkWeekView
        );

        const printSettings: PrintSettings = {
            calendarId,
            view,
            weekLayout,
            agenda,
            notes,
            miniMonth,
            fromHour,
            toHour,
            hideWorkWeekView,
        };

        // Styles
        const { styles, className, separatorSize = 30 } = props;
        const classNames = getClassNames(styles, {
            className: className,
            separatorSize: separatorSize!,
        });

        // Application Settings
        const {
            date,
            calendars,
            getSurfaceItemModel,
            getCalendarEventColorScheme,
            getCharm,
            dimissPrintDialog,
            getDetailedEvent,
            isRTL,
            firstDayOfWeek,
            showWeekNumbers,
            firstWeekOfYear,
            workWeekDays,
            numDaysInDayRange,
            hideActionButtons,
            customStyles,
            printLabel,
        } = props;

        // Calculated Properties
        const { name, events, calendarColorScheme } = calendars[calendarId] || {
            name: [''],
            events: [],
            calendarColorScheme: [],
        };

        const calendarView: CalendarViewSettings = {
            date,
            view: view,
            fromHour: fromHour,
            toHour: toHour,
            firstDayOfWeek,
            workWeekDays,
            showWeekNumbers,
            firstWeekOfYear,
            numDaysInDayRange,
        };

        React.useImperativeHandle(
            ref,
            () => ({
                print(event: any) {
                    onPrintClick(calendarColorScheme.length, view, miniMonth)(event);
                },
            }),
            [calendarColorScheme, view, miniMonth]
        );

        const onPrintSettingsChange = change => {
            if (change.calendarId != null) {
                setCalendarId(change.calendarId);
            }
            if (change.view != null) {
                setView(change.view);
            }
            if (change.weekLayout != null) {
                setWeekLayout(change.weekLayout);
            }
            if (change.agenda != null) {
                setAgenda(change.agenda);
            }
            if (change.notes != null) {
                setNotes(change.notes);
            }
            if (change.miniMonth != null) {
                setMiniMonth(change.miniMonth);
            }
            if (change.fromHour != null) {
                setFromHour(change.fromHour);
            }
            if (change.toHour != null) {
                setToHour(change.toHour);
            }
            if (change.hideWorkWeekView != null) {
                setHideWorkWeekView(change.hideWorkWeekView);
            }
        };

        const onCancelClick = (dismissPrintDialog: () => void) => (event: any) => {
            dismissPrintDialog();
            // log cancellation
            logUsage('CalendarPrintCancelled');
        };

        const onPrintClick = (calendarsLength: number, view: DateRangeType, miniMonth: boolean) => (
            event: any
        ) => {
            print(
                document.getElementById('calendarPrintableItem').innerHTML,
                getCssStyles(),
                document.documentElement.attributes
            );

            // logging 'Print' usage
            logUsage('CalendarPrint', {
                CalendarsCount: calendarsLength,
                View: view,
                HideMiniMonth: miniMonth,
            });
        };

        const getCssStyles = (): string => {
            let rulesUsed = [];

            // Calendar surface uses merged styles. In order to get all of merged styles
            // and transform them into appropriate stylesheets, we query the document stylesheets
            let sheets = document.styleSheets;
            let result = '';
            result += '@media print { html,body { margin: 0; }}' + '\n\n';

            // bug in Edge where multiple pages don't print unless the default 0 is overridden
            if (agenda) {
                result += 'html { margin: 0.01px!important }' + '\n\n';
            }
            result += '@page { margin: 0; }' + '\n\n';
            for (var j = 0; j < sheets.length; j++) {
                var sheet = document.styleSheets[j] as CSSStyleSheet;
                try {
                    if (sheet.rules || sheet.cssRules) {
                        var rules = sheet.rules || sheet.cssRules;

                        for (var r = 0; r < rules.length; r++) {
                            // We query the CSSRule.STYLE_RULE for getting all of our styles
                            // This most probably gives our theme colors, neutral colors and merged styles.
                            if (rules[r].type == CSSRule.STYLE_RULE) {
                                let styleRule = rules[r] as CSSStyleRule;
                                let selectorText = styleRule.selectorText;
                                let replacedText = selectorText.replace(' ', ',');
                                rulesUsed.push(rules[r], replacedText);
                            } else if (rules[r].type == CSSRule.FONT_FACE_RULE) {
                                // Icons that are used on surface are retrieved as FontFamily
                                // We need to query the CSSRule.FontFaceRule in order to render the iconsset.
                                let styleRule = rules[r] as CSSFontFaceRule;
                                result += styleRule.cssText + '\n\n';
                            } else {
                                continue;
                            }
                        }
                    }
                } catch (err) {}
            }

            result += rulesUsed
                .map(cssRule => {
                    if (cssRule.style) {
                        var cssText = cssRule.style.cssText;
                    } else {
                        cssText = cssRule.cssText;
                    }
                    if (cssText) {
                        let stylecssText = cssText
                            .replace(/(\{|;)\s+/g, '$1\n  ')
                            .replace(/\A\s+}/, '}');

                        if (
                            cssRule.selectorText.indexOf('.printPage-') >= 0 ||
                            cssRule.selectorText.indexOf('ffprintPage') >= 0
                        ) {
                            if (!agenda) {
                                stylecssText += 'transform: scale(1.2);';
                            }
                            stylecssText += 'border: none;';
                        }

                        return cssRule.selectorText + '{' + stylecssText + '}\n\n';
                    } else {
                        return null;
                    }
                })
                .join('\n');

            return result;
        };

        return (
            <div className={classNames.root}>
                <div className={bindClassNames('noPrint', classNames.settingsContainer)}>
                    <div style={customStyles?.label} className={classNames.printTitle}>
                        {printLabel || loc(printModalLabel)}
                    </div>
                    <CalendarPrintSettings
                        className={classNames.settingsPane}
                        calendars={calendars}
                        printSettings={printSettings}
                        onChange={onPrintSettingsChange}
                        customStyles={customStyles?.dropdown}
                    />
                    {!hideActionButtons && (
                        <div>
                            <PrimaryButton
                                className={classNames.actionButtons}
                                text={loc(calendarPrintPanelPrintLabel)}
                                onClick={onPrintClick(calendarColorScheme.length, view, miniMonth)}
                            />
                            <DefaultButton
                                className={classNames.actionButtons}
                                text={loc(calendarPrintPanelCancelLabel)}
                                onClick={onCancelClick(dimissPrintDialog)}
                            />
                        </div>
                    )}
                </div>
                <div className={classNames.container}>
                    <style type="text/css" media="print" />
                    {!hideActionButtons && (
                        <div className={classNames.closeButtonContainer}>
                            <IconButton
                                className={classNames.closeButton}
                                title={loc(printModalCloseIconTitle)}
                                iconProps={{ iconName: ControlIcons.Cancel }}
                                onClick={onCancelClick(dimissPrintDialog)}
                            />
                        </div>
                    )}
                    <CalendarPrintPreview
                        className={bindClassNames(
                            classNames.previewPane,
                            isBrowserFirefox()
                                ? classNames.paddingForPreview
                                : classNames.positionPreview
                        )}
                        title={name}
                        weekLayout={weekLayout}
                        agenda={agenda}
                        notes={notes}
                        calendarView={calendarView}
                        miniMonth={miniMonth}
                        events={events}
                        getSurfaceItemModel={getSurfaceItemModel}
                        getCalendarEventColorScheme={getCalendarEventColorScheme}
                        getCharm={getCharm}
                        calendarColorScheme={calendarColorScheme}
                        getDetailedEvent={getDetailedEvent}
                        isRTL={isRTL}
                    />
                </div>
            </div>
        );
    },
    { forwardRef: true }
);
